import { useMemo, useRef } from 'react';
import { calculateBoundingBox, Disciplines } from '~/utilities';
import { useAppDispatch, useAppSelector } from '~/state';
import { selectDiscipline, setDiscipline } from '~/state/discipline';
import { setMapBounds, setMapInteractionActive } from '~/state/mapData';
import {
  ComboBoxPrediction,
  IsMulti,
  generateDisciplineList,
} from '~/utilities';
import Select, { ActionMeta, StylesConfig } from 'react-select';
import { inputBaseStyles } from '~/utilities';
import { Control } from './components';
import { trackEvent } from '~/hooks';
import { selectLocation } from '~/state/location';
const styles: StylesConfig<ComboBoxPrediction, IsMulti> = {
  menuList: (base) => ({
    ...base,
    overflowY: 'auto',
  }),
};

type DisciplineInputProps = {
  inputId?: string;
};

export const DISCIPLINE_INPUT_ID = 'discipline-input';

export const DisciplineInput = (props: DisciplineInputProps) => {
  const dispatch = useAppDispatch();
  const discipline = useAppSelector(selectDiscipline);
  const inputRef = useRef(null);
  const location = useAppSelector(selectLocation);
  const handleChange = (
    value: ComboBoxPrediction | null,
    { action }: ActionMeta<ComboBoxPrediction | null>
  ) => {
    if (action === 'clear') {
      dispatch(setDiscipline(undefined));
      dispatch(setMapInteractionActive(false));
      return;
    }
    if (action === 'select-option') {
      dispatch(setDiscipline(value?.value));
      trackEvent('mpl_discipline_selected', { discipline: value?.value });
      dispatch(setMapInteractionActive(false));
      dispatch(
        setMapBounds(
          calculateBoundingBox(
            {
              lat: location?.coordinates?.lat || 0,
              lng: location?.coordinates?.lng || 0,
            },
            5
          )
        )
      );
    }
  };

  const value = useMemo(
    () =>
      discipline
        ? {
            label: Disciplines[discipline],
            value: discipline,
          }
        : null,
    [discipline]
  );

  const inputId = props.inputId || DISCIPLINE_INPUT_ID;

  return (
    <Select
      inputId={inputId}
      ref={inputRef}
      closeMenuOnSelect
      isClearable
      openMenuOnFocus
      blurInputOnSelect
      value={value}
      placeholder="Select"
      onChange={handleChange}
      options={generateDisciplineList()}
      maxMenuHeight={600}
      styles={{ ...inputBaseStyles, ...styles }}
      className="w-full"
      aria-label="Select a discipline to search for"
      components={{
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null,
        ClearIndicator: () => null,
        Control,
      }}
    />
  );
};

DisciplineInput.displayName = 'DisciplineInput';
